import {BodyText, FlexColumn, FlexRow, SvgIcon} from '@make-software/cspr-design';
import InfoIcon from 'assets/icons/ic-info.svg';
import React from 'react';
import styled from 'styled-components';
import { StyledTooltip } from 'app/components/common/common-styles';
import { AutomationRequest } from 'app/console-api/automations/types';
import { AutomationRegistry } from 'app/components/common/automation/registry';
import { TradeType } from 'app/console-api';

const StyledSvgIcon = styled(SvgIcon)`
  margin-left: 5px;
`;

const StyledSubtitleText = styled.div`
  font-size: 18px;
  font-weight: bold;
`;

export const AutomationRulesWrapper = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    background: '#f5f5f7',
    width: ['unset', 'unset', '556px'],
    padding: '8px 16px'
  })
);

const AutomationRulesTitle = styled(FlexColumn)`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
`;
const AutomationRulesSubTitle = styled(FlexColumn)`
  color: #84858c;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
`;
const AutomationRulesDescription = styled(FlexColumn)`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

export interface AutomationRulesProps {
  EqualTrafficConfig: Record<number, number>;
  automations: AutomationRequest[];
  tradeType: TradeType;
}

export const DealAutomationRules = ({
  EqualTrafficConfig,
  automations,
  tradeType
}: AutomationRulesProps) => {
  const renderTooltip = (title: string) => (
    <StyledTooltip tooltipContent={title}>
      <StyledSvgIcon src={InfoIcon} />
    </StyledTooltip>
  );

  return (
    <FlexColumn gap={12}>
      <StyledSubtitleText>Automation rules</StyledSubtitleText>
      {tradeType === 'exchange' && (
        <AutomationRulesWrapper gap={8}>
          {/* Header Row */}
          <FlexRow align="center">
            <AutomationRulesTitle direction="row" align="center">
              Equal Traffic Deal
              {renderTooltip(
                'Traffic from the leading source will be temporarily suspended while balancing is in progress.'
              )}
            </AutomationRulesTitle>
          </FlexRow>

          {/* Column Headers with Fixed Width */}
          <FlexRow gap={20} align="center">
            <FlexColumn style={{ minWidth: '150px', textAlign: 'left' }}>
              <AutomationRulesSubTitle direction="row">
                Amount of Traffic
                {renderTooltip('Amount of Traffic to apply rules')}
              </AutomationRulesSubTitle>
            </FlexColumn>
            <FlexColumn style={{ minWidth: '150px', textAlign: 'left' }}>
              <AutomationRulesSubTitle direction="row">
                Allowed % of Error
                {renderTooltip('Maximum allowable traffic imbalance')}
              </AutomationRulesSubTitle>
            </FlexColumn>
          </FlexRow>

          {/* Table Rows */}
          {Object.entries(EqualTrafficConfig).map(([key, value]) => (
            <FlexRow key={key} gap={20} align="center">
              <AutomationRulesDescription
                direction="row"
                style={{ minWidth: '150px', textAlign: 'left' }}>
                &gt;= {Number(key).toLocaleString()} requests
              </AutomationRulesDescription>
              <AutomationRulesDescription
                direction="row"
                style={{ minWidth: '150px', textAlign: 'left' }}>
                {value}%
              </AutomationRulesDescription>
            </FlexRow>
          ))}
        </AutomationRulesWrapper>
      )}

      { tradeType !== 'exchange' &&  automations.length === 0 && (
       <AutomationRulesWrapper>
         <BodyText size={2} variation={'darkGray'}>No rules yet</BodyText>
       </AutomationRulesWrapper>
      )}

      {automations.map((one) => {
        const meta = AutomationRegistry[one.type];
        return (
          <AutomationRulesWrapper key={one.id} gap={8}>
            <FlexRow justify="space-between">
              <AutomationRulesTitle direction="row" align="center">
                {meta.label}
              </AutomationRulesTitle>
              <AutomationRulesDescription>{meta.describe(one.params)}</AutomationRulesDescription>
            </FlexRow>
            {/* Add more details about each automation rule here if needed */}
          </AutomationRulesWrapper>
        );
      })}
    </FlexColumn>
  );
};
